import { useState } from "react";
import { ToolShell } from "@/components/ToolShell";
import { useTrackOnce } from "@/hooks/useTrackOnce";
import { useSeo } from "@/lib/seo";

const SURFACES = [
  { key: "desktop", label: "Desktop search / home", limit: 70 },
  { key: "sidebar", label: "Sidebar suggested", limit: 50 },
  { key: "mobile", label: "Mobile app", limit: 40 },
];

export default function TitleChecker() {
  useSeo("Title Truncation Checker", "Type your YouTube title and see exactly where it gets cut off on desktop, sidebar and mobile.");
  const [title, setTitle] = useState("");
  const track = useTrackOnce("title_checker");

  const onChange = (v) => {
    setTitle(v);
    if (v.length > 5) track();
  };

  const countColor = title.length <= 50 ? "var(--success)" : title.length <= 70 ? "#F5A623" : "var(--coral)";

  return (
    <ToolShell slug="title-checker" title="Title Truncation Checker" tagline="YouTube cuts titles at different lengths per surface. Type yours and see exactly what survives." hasResults={title.length > 0}>
      <div className="surface rounded-[20px] p-6 sm:p-8 glow-ring">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold">Your title</label>
          <span className="text-sm font-bold" style={{ color: countColor }} data-testid="title-char-count">{title.length} chars</span>
        </div>
        <input
          data-testid="title-checker-input"
          value={title}
          onChange={(e) => onChange(e.target.value)}
          placeholder="I Tried MrBeast's Daily Routine for 30 Days (Life-Changing Results)"
          className="w-full bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-4 py-3 text-sm outline-none focus:border-[var(--glow)]"
        />
        <p className="text-xs text-[var(--muted)] mt-2">Sweet spot: under 50 characters survives everywhere. 70+ gets cut on every surface.</p>
      </div>

      {title && (
        <div className="mt-8 space-y-3 fade-up" data-testid="truncation-results">
          {SURFACES.map((s) => {
            const cut = title.length > s.limit;
            const shown = cut ? title.slice(0, s.limit).trimEnd() : title;
            return (
              <div key={s.key} className="surface rounded-[20px] p-5" data-testid={`surface-${s.key}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs uppercase tracking-[0.15em] text-[var(--muted)] font-semibold">{s.label} <span className="normal-case tracking-normal">(~{s.limit} chars)</span></p>
                  <span className="text-xs font-bold" style={{ color: cut ? "var(--coral)" : "var(--success)" }}>
                    {cut ? `${title.length - s.limit} chars lost` : "✓ fits"}
                  </span>
                </div>
                <p className="font-semibold text-sm leading-snug">
                  {shown}
                  {cut && <span className="text-[var(--coral)]">…</span>}
                </p>
                {cut && <p className="text-xs text-[var(--muted)] mt-1.5 line-through opacity-60">{title.slice(s.limit)}</p>}
              </div>
            );
          })}
        </div>
      )}
    </ToolShell>
  );
}
